import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { DarkMode } from '@masterplandev/ui'

import { hasPermissions } from '@/access-control/hasPermissions'
import { AppManagerFetch } from '@/app/AppManagerFetch'
import { AppManagerIdentify } from '@/app/AppManagerIdentify'
import { AppManagerRedirect } from '@/app/AppManagerRedirect'
import { AppManagerScorm } from '@/app/AppManagerScorm'
import { AppManagerSSO } from '@/app/AppManagerSSO'
import { Cmi5SessionManager } from '@/app/Cmi5SessionManager'
import { StudentAppLayout } from '@/app/student/StudentAppLayout'
import { consumeFreshLoginMarker } from '@/auth/utils/freshLoginMarker'
import Announcements from '@/core/components/Announcements'
import BrowserNotification from '@/core/components/BrowserNotification'
import Loader from '@/core/components/ui/Loader/Loader'
import { LOCATION_CHANGE } from '@/core/constants/constants'
import { isStartingPage } from '@/core/constants/startingPage'
import { useNetworkNotifications } from '@/core/hooks/useNetworkNotifications'
import keycloak from '@/core/keycloak'
import { scrollToElement } from '@/core/utils/scroll/scrollToElement'
import { companyQueries } from '@/http/api/company'
import { usersQueries } from '@/http/api/users'
import { WebPushRegistration } from '@/push-notifications/components/WebPushRegistration/WebPushRegistration'

import {
  getStartingPageRedirect,
  resolveStartingPage,
  shouldApplyStartingPageRedirect,
} from './-startingPageRedirect'

export const Route = createFileRoute('/_learning')({
  beforeLoad: async ({ context: { queryClient }, location }) => {
    if (!keycloak.authenticated) {
      throw redirect({ href: keycloak.createLoginUrl() })
    }

    const user = await queryClient.ensureQueryData(
      usersQueries.usersCurrentDetails(),
    )
    if (!user) {
      throw Error('User not found')
    }

    if (!hasPermissions(user, { to: 'access-learning' })) {
      if (hasPermissions(user, { to: 'access-company' })) {
        throw redirect({ to: '/company' })
      }

      throw notFound()
    }

    // Apply the configured starting page once after login; deep links take precedence.
    const isFreshLogin = consumeFreshLoginMarker()
    const shouldResolveStartingPage = shouldApplyStartingPageRedirect({
      features: user.features,
      isFreshLogin,
      pathname: location.pathname,
    })

    if (shouldResolveStartingPage) {
      const userStartingPage = user.settings?.platform_start_page
      const companyStartingPage = isStartingPage(userStartingPage)
        ? undefined
        : await queryClient
            .ensureQueryData(companyQueries.detail())
            .then(({ default_platform_start_page }) =>
              isStartingPage(default_platform_start_page)
                ? default_platform_start_page
                : undefined,
            )
            .catch(() => undefined)

      const startingPage = resolveStartingPage(
        userStartingPage,
        companyStartingPage,
      )
      const redirectOptions = getStartingPageRedirect(startingPage)

      if (redirectOptions) {
        throw redirect(redirectOptions)
      }
    }

    return { user }
  },
  component: RouteComponent,
  pendingComponent: () => <Loader fixed />,
})

function LocationListener() {
  const location = useLocation()
  const dispatch = useDispatch()

  // This action was previously dispatched by connected-react-router
  // Since we removed it, we need to dispatch it manually because some reducers
  // depend on it.
  useEffect(() => {
    dispatch({
      type: LOCATION_CHANGE,
      // Reshape to the react-router location contract reducers expect (search as
      // a string), since TanStack's `location.search` is a parsed object.
      payload: {
        location: {
          pathname: location.pathname,
          search: location.searchStr,
          hash: location.hash,
          state: location.state,
        },
      },
    })
  }, [location, dispatch])

  useEffect(() => {
    if (location.hash) {
      scrollToElement(location.hash, { block: 'center' })
    }
  }, [location.hash])

  return null
}

function RouteComponent() {
  useEffect(() => {
    const { classList } = document.documentElement
    classList.add('student-dark-canvas')

    return () => {
      classList.remove('student-dark-canvas')
    }
  }, [])
  useNetworkNotifications()

  return (
    <>
      <LocationListener />
      <AppManagerSSO>
        <AppManagerFetch>
          <AppManagerScorm>
            <AppManagerIdentify>
              <AppManagerRedirect>
                <DarkMode>
                  <StudentAppLayout>
                    <Outlet />
                    <WebPushRegistration />
                    <Cmi5SessionManager />
                    <Announcements>
                      <BrowserNotification inner />
                    </Announcements>
                  </StudentAppLayout>
                </DarkMode>
              </AppManagerRedirect>
            </AppManagerIdentify>
          </AppManagerScorm>
        </AppManagerFetch>
      </AppManagerSSO>
    </>
  )
}
